import { Injectable } from '@angular/core';
import { CircuitDifficulty } from '@app/@shared/models/card-list';

/**
 * Service qui va agir comme un singleton pour garder en mémoire le state de notre vue de filtrage.
 * Le but va être ici de permettre à l'utilisateur de ne pas se perdre dans le filtrage quand il change de route.
 */
@Injectable({
  providedIn: 'root',
})
export class FilterCacheService {
  /**
   * Get all data from state.
   * @returns
   */
  public getState(): FilterState {
    return new FilterState(
      JSON.parse(sessionStorage.getItem('filters_input')) ?? [],
      JSON.parse(sessionStorage.getItem('main_chip_input_list')) ?? [],
      JSON.parse(sessionStorage.getItem('second_chip_input_list')) ?? [],

      JSON.parse(sessionStorage.getItem('tags_selected')) ?? [],

      JSON.parse(sessionStorage.getItem('is_dist')) ?? true,
      JSON.parse(sessionStorage.getItem('min_value')) ?? 0,
      JSON.parse(sessionStorage.getItem('max_value')) ?? 60,
      JSON.parse(sessionStorage.getItem('min_value_elevation')) ?? 0,
      JSON.parse(sessionStorage.getItem('max_value_elevation')) ?? 1000,
      JSON.parse(sessionStorage.getItem('difficulty')) ?? [],
      JSON.parse(sessionStorage.getItem('is_loop')) ?? false,
      JSON.parse(sessionStorage.getItem('is_linear')) ?? false,
      JSON.parse(sessionStorage.getItem('is_cdesi')) ?? false,

      JSON.parse(sessionStorage.getItem('start_date')) ?? null,
      JSON.parse(sessionStorage.getItem('end_date')) ?? null,
      JSON.parse(sessionStorage.getItem('is_sortie_nature')) ?? false,

      JSON.parse(sessionStorage.getItem('is_pmr')) ?? false
    );
  }

  public storeFilteredInputs(filtersInput: string[]): void {
    sessionStorage.setItem('filters_input', JSON.stringify(filtersInput));
  }

  public storeMainChipInputList(mainChipInputList: string[]): void {
    sessionStorage.setItem(
      'main_chip_input_list',
      JSON.stringify(mainChipInputList)
    );
  }

  public storeSecondChipInputList(secondChipInputList: string[]): void {
    sessionStorage.setItem(
      'second_chip_input_list',
      JSON.stringify(secondChipInputList)
    );
  }

  public storeTagsSelected(secondChipInputList: string[]): void {
    sessionStorage.setItem(
      'tags_selected',
      JSON.stringify(secondChipInputList)
    );
  }

  public storeIsDist(isDist: boolean): void {
    sessionStorage.setItem('is_dist', JSON.stringify(isDist));
  }

  public storeMinValue(minValue: number): void {
    sessionStorage.setItem('min_value', JSON.stringify(minValue));
  }

  public storeMaxValue(maxValue: number): void {
    sessionStorage.setItem('max_value', JSON.stringify(maxValue));
  }

  public storeMinValueElevation(minValue: number): void {
    sessionStorage.setItem('min_value_elevation', JSON.stringify(minValue));
  }

  public storeMaxValueElevation(maxValue: number): void {
    sessionStorage.setItem('max_value_elevation', JSON.stringify(maxValue));
  }

  public storeDifficulty(difficulty: CircuitDifficulty[]): void {
    sessionStorage.setItem('difficulty', JSON.stringify(difficulty));
  }

  public storeIsLoop(isLoop: boolean): void {
    sessionStorage.setItem('is_loop', JSON.stringify(isLoop));
  }

  public storeIsLinear(isLinear: boolean): void {
    sessionStorage.setItem('is_linear', JSON.stringify(isLinear));
  }

  public storeIsCdesi(isCdesi: boolean): void {
    sessionStorage.setItem('is_cdesi', JSON.stringify(isCdesi));
  }

  public storeStartDate(startdate: string): void {
    sessionStorage.setItem('start_date', JSON.stringify(startdate));
  }

  public storeEndDate(endDate: string): void {
    sessionStorage.setItem('end_date', JSON.stringify(endDate));
  }

  public storeIsSortieNature(isSortieNature: boolean): void {
    sessionStorage.setItem('is_sortie_nature', JSON.stringify(isSortieNature));
  }

  public storeIsPMR(isPMR: boolean): void {
    sessionStorage.setItem('is_pmr', JSON.stringify(isPMR));
  }

  // Reset state of filters
  public resetStoredValues(): void {
    this.storeFilteredInputs([]);
    this.storeMainChipInputList([]);
    this.storeSecondChipInputList([]);
    this.storeTagsSelected([]);

    this.storeIsDist(null);
    this.storeMinValue(null);
    this.storeMaxValue(null);
    this.storeMinValueElevation(null);
    this.storeMaxValueElevation(null);
    this.storeDifficulty([]);
    this.storeIsLoop(null);
    this.storeIsLinear(null);
    this.storeIsCdesi(null);

    this.storeStartDate(null);
    this.storeEndDate(null);
    this.storeIsSortieNature(null);

    this.storeIsPMR(null);
  }
}

class FilterState {
  public filtersInput: string[];
  public mainChipInputList: string[];
  public secondChipInputList: string[];

  // Correspond aux éléments associés aux checkboxes.
  public tagsSelected: string[];

  public isDist: boolean; // ? J'ai compris à quoi il servait, super. C'est le toggle switch distance/durée.
  public minValue: number;
  public maxValue: number;
  public minValueElevation: number;
  public maxValueElevation: number;
  public difficulty: CircuitDifficulty[];
  public isLoop: boolean;
  public isLinear: boolean;
  public isCdesi: boolean;

  // date list.
  public startDate: string;
  public endDate: string;
  public sortieNature: boolean;

  public isPMR: boolean;

  constructor(
    // Tags lists.
    filtersInput: string[],
    mainChipInputList: string[],
    secondChipInputList: string[],
    tagsSelected: string[],

    // Gestion distance circuits.
    isDist: boolean,
    minValue: number,
    maxValue: number,
    minValueElevation: number,
    maxValueElevation: number,
    difficulty: CircuitDifficulty[],
    isLoop: boolean,
    isLinear: boolean,
    isCdesi: boolean,

    // Gestion évènement filter.
    startDate: string,
    endDate: string,
    sortieNature: boolean,

    isPMR: boolean
  ) {
    this.filtersInput = filtersInput;
    this.mainChipInputList = mainChipInputList;
    this.secondChipInputList = secondChipInputList;
    this.tagsSelected = tagsSelected;

    this.isDist = isDist;
    this.minValue = minValue;
    this.maxValue = maxValue;
    this.minValueElevation = minValueElevation;
    this.maxValueElevation = maxValueElevation;
    this.difficulty = difficulty;
    this.isLoop = isLoop;
    this.isLinear = isLinear;
    this.isCdesi = isCdesi;

    this.startDate = startDate;
    this.endDate = endDate;
    this.sortieNature = sortieNature;

    this.isPMR = isPMR;
  }
}
